import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { VERTICALS } from "@postman/core/verticals";
import { VerticalCardField } from "@/components/pixel";
import { Accent, StatePill } from "@/components/pipeline";
import { Callout, PageTitle, Pill } from "@/components/ui";
import { applicationsQuery, type ApplicationItem } from "@/lib/applications";

/**
 * The picker.
 *
 * Seven cards, each running its own vertical's law. The card is the whole
 * link — a card with a button in the corner makes the other 90% of it dead
 * space that looks clickable.
 */
export const Route = createFileRoute("/_auth/_onboarded/apply/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(applicationsQuery),
  component: Picker,
});

const BY_SLUG = new Map(VERTICALS.map((v) => [v.slug, v]));

function Picker() {
  const { data } = useSuspenseQuery(applicationsQuery);

  return (
    <div className="flex flex-col gap-10">
      <PageTitle
        eyebrow={data.cycle ? data.cycle.name : "No cycle open"}
        title="Where do you want to build?"
        lede="Apply to as many verticals as you actually want to work in. One profile goes with all of them; each has its own form and its own rounds."
      />

      {!data.cycle ? (
        <Callout>
          Recruitment is closed at the moment. Nothing here is open, and your
          profile is saved for when the next cycle starts.
        </Callout>
      ) : null}

      {/* Bordered cells with a real gap, not the hairline-joined grid the
          site uses. Seven items in a three-column grid leaves two cells empty,
          and a joined grid paints its own background through them — two grey
          rectangles that look like a failed render. */}
      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {data.items.map((item) => (
          <VerticalCard key={item.slug} item={item} />
        ))}
      </div>
    </div>
  );
}

function VerticalCard({ item }: { item: ApplicationItem }) {
  const meta = BY_SLUG.get(item.slug as never);
  const app = item.application;
  const closed = !item.open;

  const body = (
    <Accent slug={item.slug} className="group relative flex h-full flex-col border border-line bg-paper-2">
      {/* No field behind a list, but a card is not a list — this one is
          ~120px of texture at the top and nothing scrolls under it. */}
      <div className="relative h-[110px] overflow-hidden border-b border-line">
        {meta ? (
          <VerticalCardField law={meta.law} accent={meta.accent} className="absolute inset-0" />
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-4 p-6">
        <div className="flex items-start justify-between gap-3">
          <h2 className="display text-[24px]">{item.name}</h2>
          {app ? (
            <StatePill state={app.state} />
          ) : closed ? (
            <Pill>Not open</Pill>
          ) : (
            <Pill tone="live">Open</Pill>
          )}
        </div>

        {meta ? (
          <p className="flex-1 text-[15px] leading-relaxed text-ink-2">{meta.blurb}</p>
        ) : (
          <div className="flex-1" />
        )}

        <p className="mono text-ink-3">
          {app
            ? app.state === "in_progress"
              ? "Started — not sent yet"
              : `${app.cleared} of ${app.rounds.length} ${
                  app.rounds.length === 1 ? "round" : "rounds"
                } cleared`
            : closed
              ? "No form yet"
              : "Apply"}
        </p>
      </div>
    </Accent>
  );

  // A vertical with no application round has no page worth opening: there is
  // no form, no rounds and nothing to say beyond what the card already says.
  if (closed && !app) {
    return <div className="opacity-60">{body}</div>;
  }

  return (
    <Link
      to="/apply/$vertical"
      params={{ vertical: item.slug }}
      className="transition-opacity hover:opacity-90"
    >
      {body}
    </Link>
  );
}
